import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { TaskUserModel } from '../../tasks/models/TaskUserModel';
import { retrieveStaffUsers } from '../../tasks/services/TaskCRUD';
import { LeadsWindowModel } from '../models/LeadsWindowModel';
import { listLeadsWindows } from '../services/listLeadsWindows';
import { deleteLeadsWindow } from '../services/deleteLeadsWindow';
import { ACQUISITION_GROUP_ID } from '../../tasks/redux/TaskSaga';
import ModalLeadsWindowForm from './ModalLeadsWindowForm';

export default function LeadsWindowList() {
  const intl = useIntl();
  const [entries, setEntries] = useState<Array<LeadsWindowModel>>([]);
  const [agents, setAgents] = useState<Array<TaskUserModel>>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEntry, setEditingEntry] = useState<LeadsWindowModel | null>(
    null
  );

  async function fetchEntries() {
    setLoading(true);
    try {
      const { data } = await listLeadsWindows();
      setEntries(data);
    } catch {
      toast.error(intl.formatMessage({ id: 'leadsWindows.fetchError' }));
    } finally {
      setLoading(false);
    }
  }

  async function fetchAgents() {
    try {
      const { data } = await retrieveStaffUsers(ACQUISITION_GROUP_ID);
      setAgents(data);
    } catch {
      toast.error(intl.formatMessage({ id: 'leadsWindows.fetchAgentsError' }));
    }
  }

  async function handleDelete(id: number) {
    const confirmed = window.confirm(
      intl.formatMessage({ id: 'leadsWindows.deleteConfirm' })
    );
    if (!confirmed) return;
    try {
      await deleteLeadsWindow(id);
      toast.success(intl.formatMessage({ id: 'leadsWindows.deleteSuccess' }));
      fetchEntries();
    } catch {
      toast.error(intl.formatMessage({ id: 'leadsWindows.deleteError' }));
    }
  }

  function handleEdit(entry: LeadsWindowModel) {
    setEditingEntry(entry);
    setModalVisible(true);
  }

  function handleAdd() {
    setEditingEntry(null);
    setModalVisible(true);
  }

  function handleModalClose() {
    setModalVisible(false);
    setEditingEntry(null);
  }

  useEffect(() => {
    fetchEntries();
    fetchAgents();
  }, []);

  function getAgentName(agentId: number): string {
    const agent = agents.find((a) => a.id === agentId);
    return agent ? `${agent.name} (${agent.email})` : String(agentId);
  }

  function getShiftLabel(shift: LeadsWindowModel['shift']): string {
    return intl.formatMessage({
      id:
        shift === 'morning'
          ? 'leadsWindows.shiftMorning'
          : 'leadsWindows.shiftAfternoon',
    });
  }

  return (
    <div className="card p-10">
      <div className="mb-8 d-flex justify-content-between align-items-center">
        <h2>{intl.formatMessage({ id: 'leadsWindows.title' })}</h2>
        <button className="btn btn-primary" onClick={handleAdd}>
          {intl.formatMessage({ id: 'leadsWindows.addEntry' })}
        </button>
      </div>
      <main>
        {loading ? (
          <div className="text-center">
            <span className="spinner-border spinner-border-sm text-primary" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-muted text-center">
            {intl.formatMessage({ id: 'leadsWindows.noResults' })}
          </p>
        ) : (
          <table className="table table-striped align-middle">
            <thead className="fw-bolder">
              <tr>
                <th>{intl.formatMessage({ id: 'leadsWindows.agent' })}</th>
                <th>{intl.formatMessage({ id: 'leadsWindows.shift' })}</th>
                <th>{intl.formatMessage({ id: 'leadsWindows.fromDate' })}</th>
                <th>{intl.formatMessage({ id: 'leadsWindows.toDate' })}</th>
                <th>{intl.formatMessage({ id: 'leadsWindows.actions' })}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{getAgentName(entry.sale_agent)}</td>
                  <td>{getShiftLabel(entry.shift)}</td>
                  <td>{entry.from_date}</td>
                  <td>{entry.to_date}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary me-2"
                      onClick={() => handleEdit(entry)}
                    >
                      <i className="fas fa-edit me-1" />
                      {intl.formatMessage({ id: 'leadsWindows.edit' })}
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(entry.id)}
                    >
                      <i className="fas fa-trash-alt me-1" />
                      {intl.formatMessage({ id: 'leadsWindows.delete' })}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>
      <ModalLeadsWindowForm
        visible={modalVisible}
        onClose={handleModalClose}
        onSuccess={fetchEntries}
        agents={agents}
        editingEntry={editingEntry}
      />
    </div>
  );
}
